import { SectionResponseDtoSection } from '@/lib/models';
import type { SectionResponseDto } from '@/lib/models';

/** Section keys in their stable enum form. */
export type MarketSectionKey = (typeof SectionResponseDtoSection)[keyof typeof SectionResponseDtoSection];

/** A resolved section ready to render: its key + initial open state. */
export interface ResolvedSection {
  section: MarketSectionKey;
  /** Initial expanded state from the admin `defaultOpen` flag (§5.4 / §7.1). */
  defaultOpen: boolean;
}

/**
 * Resolve the admin-driven section order + default state (ARC-82 §5.4 / §7.1).
 *
 * Sorts the config rows by `sortOrder` ascending (ties broken by section name
 * for determinism) and projects each to `{ section, defaultOpen }`. Unknown
 * section keys are dropped so a future server enum value can't crash the page.
 * Returns `[]` for missing/empty config (the page then renders no sections).
 */
export function orderSections(config: SectionResponseDto[] | undefined): ResolvedSection[] {
  if (!config || config.length === 0) return [];

  const known = new Set<string>(Object.values(SectionResponseDtoSection));

  return config
    .filter((row) => known.has(row.section))
    .slice()
    .sort((a, b) => (a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.section.localeCompare(b.section)))
    .map((row) => ({ section: row.section, defaultOpen: row.defaultOpen }));
}
